import { useRef, useCallback, useEffect } from 'react';
import { Notification } from '../types/notification';

const DEBOUNCE_MS = 500;
const MAX_BATCH_SIZE = 50;

export const useSeenTracker = (
  notifications: Notification[],
  isPanelOpen: boolean,
  markSeen: (ids: string[]) => void,
) => {
  const seenQueue = useRef<Set<string>>(new Set());
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverCleanups = useRef<Map<string, () => void>>(new Map());
  const markSeenRef = useRef(markSeen);
  markSeenRef.current = markSeen;
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;

  const flushQueue = useCallback(() => {
    if (seenQueue.current.size === 0) return;
    const ids = Array.from(seenQueue.current).slice(0, MAX_BATCH_SIZE);
    seenQueue.current.clear();
    markSeenRef.current(ids);
  }, []);

  // Flush pending queue when panel closes
  useEffect(() => {
    if (!isPanelOpen) {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      flushQueue();
    }
  }, [isPanelOpen, flushQueue]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      hoverCleanups.current.forEach((cleanup) => cleanup());
      hoverCleanups.current.clear();
    };
  }, []);

  const trackRef = useCallback(
    (id: string) => (el: HTMLDivElement | null) => {
      // Remove previous listener for this id
      const prevCleanup = hoverCleanups.current.get(id);
      if (prevCleanup) {
        prevCleanup();
        hoverCleanups.current.delete(id);
      }

      if (!el) return;

      const handleMouseEnter = () => {
        const notification = notificationsRef.current.find((n) => n._id === id);
        if (notification?.status === 'UNREAD') {
          seenQueue.current.add(id);
          if (debounceTimer.current) clearTimeout(debounceTimer.current);
          debounceTimer.current = setTimeout(flushQueue, DEBOUNCE_MS);
        }
      };

      el.addEventListener('mouseenter', handleMouseEnter);
      el.addEventListener('touchstart', handleMouseEnter, { passive: true });
      hoverCleanups.current.set(id, () => {
        el.removeEventListener('mouseenter', handleMouseEnter);
        el.removeEventListener('touchstart', handleMouseEnter);
      });
    },
    [flushQueue],
  );

  return { trackRef };
};
